import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { blackScholesApi } from '../../services/api';
import styled from 'styled-components';

const FormContainer = styled.div`
  background-color: var(--card-bg-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.5' : '0.1'});
  padding: 1.5rem;
  color: var(--text-color);
  border: 1px solid var(--input-border-color);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  @media (max-width: 1200px) {
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 6px;
  }
`;

const FormTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-weight: 600;
  transition: color 0.3s ease;
`;

const FormGroup = styled.div`
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 0.25rem;
    font-weight: 500;
    color: var(--text-color);
    font-size: 0.9rem;
    transition: color 0.3s ease;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.4rem;

    label {
      font-size: 0.875rem;
      margin-bottom: 0.2rem;
    }
  }
`;

const OptionsParametersContainer = styled.div`
  background-color: var(--card-bg-color);
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
  }
`;

const ParameterGroup = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const TickerGroup = styled(FormGroup)`
  display: flex;
  gap: 0.5rem;
  
  input {
    flex: 1;
  }
  
  button {
    padding: 0.75rem 1rem;
    background-color: var(--button-primary-bg);
    color: var(--button-primary-text);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
    
    &:hover {
      background-color: ${props => props.theme.mode === 'dark' ? '#4a72b7' : '#1a4288'};
    }
    
    &:disabled {
      background-color: ${props => props.theme.mode === 'dark' ? '#404040' : '#cccccc'};
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.mode === 'dark' ? '#ff8080' : '#d32f2f'};
  font-size: 0.875rem;
  margin-top: 0.5rem;
  transition: color 0.3s ease;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.95rem;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 0.3rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--input-border-color);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--input-bg-color);
  color: var(--input-text-color);
  min-height: 42px;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--button-primary-bg);
    box-shadow: 0 0 0 2px ${props => props.theme.mode === 'dark' ? 'rgba(91, 157, 255, 0.2)' : 'rgba(74, 144, 226, 0.2)'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.mode === 'dark' ? '#2d2d2d' : '#f5f5f5'};
    color: ${props => props.theme.mode === 'dark' ? '#a0a0a0' : '#666'};
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
    margin-left: 0.5rem;
  }

  @media (max-width: 1200px) {
    padding: 0.7rem;
    font-size: 0.95rem;
    min-height: 40px;
  }

  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.9rem;
    min-height: 38px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  color: var(--text-color);
  transition: color 0.3s ease;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 1200px) {
    gap: 0.875rem;
    margin-top: 1.25rem;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-top: 1rem;
  }

  button {
    min-height: 42px;
    
    @media (max-width: 1200px) {
      min-height: 40px;
    }

    @media (max-width: 768px) {
      min-height: 38px;
    }
  }
`;

const CalculateButton = styled.button`
  flex: 1;
  background-color: ${props => props.theme.mode === 'dark' ? '#5b9dff' : '#4a90e2'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a7dcc' : '#357ABD'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.mode === 'dark' ? '#404040' : '#cccccc'};
    cursor: not-allowed;
  }
`;

const ResetButton = styled.button`
  flex: 1;
  background-color: ${props => props.theme.mode === 'dark' ? '#2a2a2a' : '#f5f5f5'};
  color: ${props => props.theme.mode === 'dark' ? '#e0e0e0' : '#333'};
  border: 1px solid var(--input-border-color);
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#333' : '#e8e8e8'};
    border-color: ${props => props.theme.mode === 'dark' ? '#555' : '#ccc'};
  }
`;

const InputForm = ({ inputs, onInputChange, onReset, isLoading }) => {
  const { theme } = useTheme();

  const [localInputs, setLocalInputs] = useState(inputs);
  const [isDirty, setIsDirty] = useState(false);
  
  const activeFieldRef = useRef(null);



  useEffect(() => {
    if (!isDirty) {
      setLocalInputs(inputs);
    }
  }, [inputs, isDirty]);
  
  const handleFocus = (e) => {
    activeFieldRef.current = e.target.name;
    setIsDirty(true);
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setIsDirty(true);
    activeFieldRef.current = name;
    
    if (type === 'number') {
      setLocalInputs(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (type === 'checkbox') {
      setLocalInputs(prev => ({
        ...prev,
        [name]: checked
      }));
      onInputChange(name, checked);
    } else {
      setLocalInputs(prev => ({
        ...prev,
        [name]: value
      }));
      onInputChange(name, value);
    }
  };
  
  const handleBlur = (e) => {
    const { name, value, type } = e.target;
    
    if (activeFieldRef.current === name) {
      activeFieldRef.current = null;
      setIsDirty(false);
    }
    
    if (type === 'number') {
      if (value === '') {
        setLocalInputs(prev => ({
          ...prev,
          [name]: '0'
        }));
        onInputChange(name, 0);
      } else {
        onInputChange(name, parseFloat(value) || 0);
      }
    }
  };
  
  return (
    <FormContainer theme={theme}>
      <FormTitle>Input Parameters</FormTitle>
      

      
      <FormGroup>
        <Label htmlFor="stock_price">Stock Price ($)</Label>
        <Input
          type="number"
          id="stock_price"
          name="stock_price"
          value={localInputs.stock_price}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          min="0.01"
          step="any"
          disabled={isLoading}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="strike_price">Strike Price ($)</Label>
        <Input
          type="number"
          id="strike_price"
          name="strike_price"
          value={localInputs.strike_price}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          min="0.01"
          step="any"
          disabled={isLoading}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="time_to_expiry">Time to Expiry</Label>
        <Input
          type="number"
          id="time_to_expiry"
          name="time_to_expiry"
          value={localInputs.time_to_expiry}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          min="0.01"
          step="any"
          disabled={isLoading}
        />
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="time_in_days"
            name="time_in_days"
            checked={localInputs.time_in_days}
            onChange={handleChange}
            disabled={isLoading}
          />
          <CheckboxLabel htmlFor="time_in_days">
            Express in days (rather than years)
          </CheckboxLabel>
        </CheckboxContainer>
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="risk_free_rate">Risk-Free Rate (decimal)</Label>
        <Input
          type="number"
          id="risk_free_rate"
          name="risk_free_rate"
          value={localInputs.risk_free_rate}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          min="0"
          max="10"
          step="any"
          disabled={isLoading}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="volatility">Volatility (decimal)</Label>
        <Input
          type="number"
          id="volatility"
          name="volatility"
          value={localInputs.volatility}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          min="0.01"
          max="10"
          step="any"
          disabled={isLoading}
        />
      </FormGroup>
      

      
      <ButtonContainer>
        <ResetButton
          type="button"
          onClick={onReset}
          disabled={isLoading}
        >
          Reset
        </ResetButton>
      </ButtonContainer>
    </FormContainer>
  );
};

export default InputForm;
